import React, { useContext, useEffect, useMemo, useState } from 'react';
// eslint-disable-next-line id-length
import _ from 'lodash';
import { Typography } from '@one-thd/sui-atomic-components';

import { Placeholder } from '@thd-olt-component-react/core-ui';

import { EstimateApiContext } from '../context/EstimateApiContext';
import { RentalEstimateContext } from '../context/RentalEstimateContext';
import {
  getDurationMessage,
  vehicleCheck
} from '../utils/helper';
import { Fees } from '../components/Fees';
import {
  formatCurrency,
  formatPercentage,
  getSalesTaxPercentage,
  getHasDiscount
} from '../utils/rate-helper';

export const RentalEstimate = () => {
  const { data } = useContext(EstimateApiContext);
  const {
    accessories,
    pickupTime,
    returnTime,
    rentalCategory,
    damageProtection,
    hideDeposit,
    hideSuccessMsg = false,
    hideDue = false,
    deliveryAddress,
  } = useContext(RentalEstimateContext);
  const [localTotals, setLocalTotals] = useState({});

  const isAVehicle = vehicleCheck(rentalCategory);

  const isDataLoaded = (!_.isEmpty(data));

  const isDelivery = (!_.isEmpty(deliveryAddress));

  const selectedAccessories = useMemo(() => (
    accessories.filter((accessory) => accessory.selected)
  ), [accessories]);

  const hasDiscount = useMemo(() => getHasDiscount(data), [data]);

  useEffect(() => {
    if (!_.isEmpty(data)) {
      const selectedAccessoriesPrice = selectedAccessories.reduce(
        (acc, accessory) => acc + parseFloat(accessory?.price)
        , 0
      );

      const estimatedTax = damageProtection
        ? data?.totals?.totalEstimateTax
        : data?.totals?.totalEstimateWithoutDamageWaiverTax;

      const estimatedTotal = (damageProtection
        ? data?.totals?.totalEstimateWithTax
        : data?.totals?.totalEstimateWithoutDamageWaiverWithTax) + selectedAccessoriesPrice;

      const deposit = hasDiscount
        ? data?.totals?.totalDiscountedDeposit : data?.totals?.totalDeposit;

      const balanceDue = estimatedTotal - deposit;

      setLocalTotals({
        estimatedTax,
        estimatedTotal,
        deposit,
        balanceDue
      });
    } else if (_.isEmpty(data) && !_.isEmpty(localTotals)) {
      setLocalTotals({});
    }
  }, [data, damageProtection, hasDiscount, selectedAccessories]);

  return (
    <div className="sui-px-4" data-component="RentalEstimate">
      <div className="sui-grid sui-grid-cols-4 sui-pt-6 sui-min-w-full sui-border-primary sui-border-solid sui-pb-4">

        {(isDataLoaded && !hideSuccessMsg) && (
          <div className="sui-justify-self-start sui-col-span-6">
            <Typography color="success">
              We found you the best price for your rental period.
            </Typography>
          </div>
        )}

        <div className="sui-justify-self-start sui-col-span-6">
          <Typography data-automation-id="rentalPeriod_label" weight="bold">Rental Period</Typography>
        </div>
        <div className="sui-justify-self-start sui-col-span-5 sui-mr-4">
          {!isDataLoaded
            ? (
              <Placeholder type="rect" height="17px" width="170px" />
            ) : (
              <Typography data-automation-id="rentalPeriod_time">
                {getDurationMessage(pickupTime, returnTime)}
              </Typography>
            )}
        </div>
        <div className="sui-inline-flex sui-justify-self-end">
          {!isDataLoaded
            ? (
              <Placeholder type="rect" height="17px" width="50px" />
            ) : (
              <Typography data-automation-id="rentalPeriod_value">
                {formatCurrency(data?.totals?.totalTools)}
              </Typography>
            )}
        </div>

        {isAVehicle && (
          <>
            <div className="sui-justify-self-start sui-col-span-6 sui-mt-2">
              <Typography data-automation-id="mileage_label" weight="bold">Mileage</Typography>
            </div>
            <div className="sui-justify-self-start sui-col-span-5">
              {!isDataLoaded
                ? (
                  <Placeholder type="rect" height="17px" width="50px" />
                ) : (
                  <Typography data-automation-id="unlimited_label">Unlimited</Typography>
                )}
            </div>
            <div className="sui-inline-flex sui-justify-self-end">
              {!isDataLoaded
                ? (
                  <Placeholder type="rect" height="17px" width="50px" />
                ) : (
                  <Typography data-automation-id="free_label" color="success">Free</Typography>
                )}
            </div>
          </>

        )}

        {
          selectedAccessories && selectedAccessories.length > 0 && (
            <>
              <div className="sui-justify-self-start sui-col-span-6 sui-mt-2">
                <Typography data-automation-id="mileage_label" weight="bold">Accessories</Typography>
              </div>

              {selectedAccessories.map((accessory) => (
                <React.Fragment key={accessory.id}>
                  <div className="sui-justify-self-start sui-col-span-5" key={accessory.id}>
                    {!isDataLoaded ? (
                      <Placeholder type="rect" height="17px" width="50px" />
                    ) : (
                      <Typography data-automation-id="unlimited_label">{accessory.description}</Typography>
                    )}
                  </div>

                  <div className="sui-inline-flex sui-justify-self-end">
                    { // eslint-disable-next-line no-nested-ternary
                      !isDataLoaded
                        ? (
                          <Placeholder type="rect" height="17px" width="50px" />
                        ) : (
                          parseFloat(accessory?.price) !== 0 ? (
                            <Typography data-automation-id="rentalPeriod_value">
                              {formatCurrency(accessory?.price)}
                            </Typography>
                          ) : (
                            <Typography data-automation-id="free_label" color="success">Free</Typography>
                          )
                        )
                    }
                  </div>
                </React.Fragment>
              ))}
            </>
          )
        }

        <Fees />

        <div className="sui-justify-self-start sui-col-span-6 sui-mt-2">
          <Typography weight="bold" data-automation-id="taxes_label">Taxes</Typography>
        </div>
        <div className="sui-justify-self-start sui-col-span-5">
          {!isDataLoaded
            ? (
              <Placeholder type="rect" height="17px" width="100px" />
            ) : (
              <Typography data-automation-id="salesTax_label">
                Sales Tax ({formatPercentage(getSalesTaxPercentage(data))})
              </Typography>
            )}
        </div>
        <div className="sui-inline-flex sui-justify-self-end">
          {!isDataLoaded
            ? (
              <Placeholder type="rect" height="17px" width="50px" />
            ) : (
              <Typography data-automation-id="salesTax_value">
                {formatCurrency(localTotals?.estimatedTax)}
              </Typography>
            )}
        </div>
      </div>

      <div className="sui-grid sui-grid-cols-4 sui-pt-6 sui-min-w-full
      sui-border-t-1 sui-border-primary sui-border-solid"
      >
        <div className="sui-justify-self-start sui-col-span-3">
          <Typography data-automation-id="estimatedTotal_label" weight="bold">
            {isDelivery && !hideDue ? 'Estimated Total* - Due Now' : 'Estimated Total'}
          </Typography>
        </div>

        <div className="sui-inline-flex sui-justify-self-end">
          {!isDataLoaded
            ? <Placeholder type="rect" height="17px" width="50px" />
            : (
              <Typography data-automation-id="estimatedTotal_value">
                {formatCurrency(localTotals?.estimatedTotal)}
              </Typography>
            )}
        </div>

        {(!hideDeposit && !isDelivery) && (
          <>
            <div className="sui-justify-self-start sui-col-span-3 sui-py-2 sui-py-1">
              <Typography data-automation-id="depositOrCreditCardHoldDue_label" color="danger">
                {hasDiscount
                  ? `PRO Xtra Deposit or Credit Card Hold ${!hideDue ? 'Due' : ''}`
                  : `Deposit or Credit Card Hold ${!hideDue ? 'Due' : ''}`}
              </Typography>
            </div>
            <div className="sui-inline-flex sui-justify-self-end sui-py-2 sui-py-1">
              {!isDataLoaded
                ? <Placeholder type="rect" height="17px" width="50px" />
                : (
                  <Typography data-automation-id="depositOrCreditCardHoldDue_value" color="danger">
                    {formatCurrency(localTotals?.deposit)}
                  </Typography>
                )}
            </div>

            <div className="sui-justify-self-start sui-col-span-3">
              <Typography data-automation-id="totalDueAtDropoff_label">Total Due at Drop-off</Typography>
            </div>
            <div className="sui-inline-flex sui-justify-self-end">
              {!isDataLoaded
                ? <Placeholder type="rect" height="17px" width="50px" />
                : (
                  <Typography data-automation-id="totalDueAtDropoff_value">
                    {formatCurrency(localTotals?.balanceDue)}
                  </Typography>
                )}
            </div>
          </>
        )}
      </div>

      <div className="sui-pb-6 sui-pt-4">
        <Typography variant="body-xs" data-automation-id="finalcosts_value">
          {isDelivery
            ? '*Final costs determined upon unit pickup at the end of your rental period.'
            : '*Final costs will be determined when you return your rental.'}
        </Typography>
      </div>
    </div>
  );
};

RentalEstimate.propTypes = {};

RentalEstimate.defaultProps = {};

RentalEstimate.displayName = 'RentalEstimate';

RentalEstimate.dataModel = {};
